// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import { sortBy, isEqual } from 'lodash';
import { useApp } from '../hooks/use-app';
import Button from '../shared/button';
import PermissionsEditor from '../shared/permissions-editor';
import { Item } from '../shared/select-two-column';
import { Role } from '../models/role';
import { Member } from '../models/member';
import { Memberships, LayerMembers, OwnerRoles } from './api/types';

interface Props {
  layer_id: Readonly<string | null>;
  roles: ReadonlyArray<Role>;
}

export const rolesFormat = (userRoles: ReadonlyArray<Role>): ReadonlyArray<Item> => {
  const currentUserRoles = userRoles.map((role) => ({
    label: role.name,
    value: role.id,
    is_owner: role.can_manage_roles,
  }));

  currentUserRoles.unshift({
    label: 'All Roles',
    value: null,
    is_owner: false,
  });

  return sortBy(currentUserRoles, 'label');
};

export const membersFormat = (
  members: Memberships,
  layerMembers, ownerRoles: OwnerRoles,
) => members.map((member) => {
  const layerMember = layerMembers.find((item) => item.membership_id === member.id);
  return {
    email: member.email,
    name: member.user ?? null,
    id: member.id,
    role_name: member.role_name,
    is_disabled: ownerRoles?.includes(member.role_name) || layerMember?.from_group || false,
    from_group: layerMember?.from_group ?? false,
  };
});

export const getOwners = (memberships: Memberships, ownerRoles: OwnerRoles) => memberships
  .filter((member) => ownerRoles.includes(member.role_name))
  .map((member) => member.id);

export const getFromGroup = (memberships:Memberships) => memberships
  .filter((member) => member.from_group)
  .map((member) => member.id);
export const selectedFormat = (
  memberships: Memberships,
  ownerRoles: OwnerRoles,
  isEditing: Readonly<string>,
) => {
  const selectedMembers = memberships.map((member) => member.membership_id);
  // filters non-owners when creating a new layer
  const owners = getOwners(memberships, ownerRoles);
  const groupManaged = getFromGroup(memberships);
  const editSelected = Array.from(new Set([...owners, ...groupManaged, ...selectedMembers]));
  const newSelected = Array.from(new Set([...owners, ...groupManaged]));
  return isEditing ? editSelected : newSelected;
};

export const isSelected = (id: Readonly<string>, memberships: ReadonlyArray<string>): boolean => memberships.includes(id);
export const hasChanges = (changes: ReadonlyArray<string>, initial: ReadonlyArray<string>) => !isEqual(sortBy(changes), sortBy(initial));

const PermissionLayers: React.FC<Props> = ({ roles, layer_id }: Readonly<Props>) => {
  const app = useApp();

  const ownerRoles = roles.filter((role) => role.can_manage_roles).map((r) => r.name);
  const currentUserRoles = useMemo(() => rolesFormat(roles), []);

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<LayerMembers>({
    all: [],
    selected: [],
    initial: [],
  });

  useEffect(() => {
    async function setupPermissions() {
      try {
        await setPermissions();
      } catch (error) {
        console.error('Error while calling setting layer permission:', error);
      }
    }
    setupPermissions();
  }, []);

  const setPermissions = async () => {
    const memberships: Readonly<Member[]> = await fetchMemberships();
    let layerMemberships = memberships;

    // eslint-disable-next-line camelcase
    if (layer_id) {
      layerMemberships = await fetchLayerMembers();
      layerMemberships = layerMemberships.map((layerMember) => {
        const matchMember = memberships.find((member) => member.id === layerMember.membership_id);

        return {
          ...layerMember,
          role_name: matchMember?.role_name ?? null,
        };
      });
    }
    setMembers({
      all: membersFormat(memberships, layerMemberships, ownerRoles),
      selected: selectedFormat(layerMemberships, ownerRoles, layer_id),
      initial: selectedFormat(layerMemberships, ownerRoles, layer_id),
    });
    setLoading(false);
  };

  const fetchMemberships = async () => {
    const { objects } = await app.api.memberships.all({ per_page: 5000 });
    return sortBy(objects, 'first_name');
  };

  const fetchLayerMembers = async () => {
    const { objects } = await app.api.permissions.all({ type: 'layer_members', object_id: layer_id });
    return sortBy(objects, 'first_name');
  };

  const handlePermissionsDone = (changes: ReadonlyArray<string>) => {
    if (hasChanges(changes, members.initial)) {
      setMembers({ ...members, selected: changes });
    }

    setShow(false);
  };

  const handlePermissionsCancel = () => {
    setMembers({ ...members, selected: members.initial });
    setShow(false);
  };

  // Removes owners from selected for inputs. The API only posts non-owner IDs
  const ownerIDsToOmit = useMemo(() => members.all && getOwners(members.all, ownerRoles), [members.all]);
  const nonOwnerIDs = useMemo(() => members.selected.filter((selected) => !ownerIDsToOmit.includes(selected)), [members.selected]);

  return (
    <>
      <label>Manage Permissions</label>
      <Button link affirm small onClick={() => !loading && setShow(true)}>
        Select Members
      </Button>

      {/* NOTE: Temporary inputs */}
      {nonOwnerIDs.map((id, i) => (
        <input
          key={i}
          style={{ display: 'none' }}
          type="checkbox"
          name="membership_ids[]"
          value={id}
          defaultChecked={isSelected(id, members.selected)}
        />
      ))}

      <PermissionsEditor
        show={show}
        type="members"
        loading={loading}
        successAlert={false}
        objects={members.all}
        selectedIDs={members.selected}
        roles={currentUserRoles}
        onDone={handlePermissionsDone}
        onCancel={handlePermissionsCancel}
      />
    </>
  );
};

export default PermissionLayers;
